import { useState } from "react";
import { FileText, FileSpreadsheet, FileImage, File } from "lucide-react";
import { Highlight, themes } from "prism-react-renderer";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { CopyButton } from "~/components/assistant/CopyButton";
import { DetailField } from "~/components/assistant/messages/DetailField";
import { DetailsSection } from "~/components/assistant/messages/DetailsSection";
import { LineageTab } from "~/components/assistant/messages/LineageTab";
import type { TextFile, BlobFile } from "~/components/assistant/types";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { trackEvent } from "~/utils/monitoring";

type FileArtifact = TextFile | BlobFile;

interface FileMessageProps {
  file: FileArtifact;
  artifactType: "text_file" | "blob_file";
  debugMode?: boolean;
}

type TabType = "file" | "code" | "lineage";

interface Tab {
  id: TabType;
  label: string;
}

/** Pick an icon based on file extension */
function getFileIcon(fileName: string) {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  if (["csv", "tsv", "xlsx", "xls", "parquet"].includes(ext)) {
    return <FileSpreadsheet className="w-5 h-5" />;
  }
  if (["png", "jpg", "jpeg", "gif", "svg", "webp", "bmp", "tiff"].includes(ext)) {
    return <FileImage className="w-5 h-5" />;
  }
  if (["txt", "md", "json", "xml", "yaml", "yml", "log", "html", "css", "js", "py"].includes(ext)) {
    return <FileText className="w-5 h-5" />;
  }
  return <File className="w-5 h-5" />;
}

/** Get a human-readable label for file type */
function getFileTypeLabel(fileName: string): string {
  const ext = fileName.split(".").pop()?.toUpperCase();
  return ext ? `${ext} file` : "File";
}

export function FileMessage({ file, artifactType, debugMode = false }: FileMessageProps) {
  const [activeTab, setActiveTab] = useState<TabType>("file");
  const { conversationId, contextId } = useAssistantContext();

  const { isLoading, Prism: PrismInstance } = usePrismLanguages(["python"]);

  const { file_name } = file.content;

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);

    trackEvent("analysis_agent_artifact_tab_viewed", {
      dataset_id: contextId || "Unknown",
      conversation_id: conversationId || undefined,
      artifact_id: file.artifact_id || undefined,
      artifact_type: artifactType,
      context: "dataset",
      tab,
    });
  };

  const tabs: Tab[] = [{ id: "file", label: "File" }];
  if (file?.code) {
    tabs.push({ id: "code", label: "Code" });
  }
  if (file?.artifact_id) {
    tabs.push({ id: "lineage", label: "Lineage" });
  }

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-4 pt-1 w-full"
      aria-label="File artifact display"
    >
      {/* Tabs */}
      <div
        className="flex border-b border-gray-200 mb-4"
        role="tablist"
        aria-label="File artifact tabs"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            onClick={() => handleTabChange(tab.id)}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors cursor-pointer ${
              activeTab === tab.id
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="overflow-x-auto">
        {activeTab === "file" && (
          <div role="tabpanel" id="tabpanel-file" aria-label="File content">
            {/* File card */}
            <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-md bg-white border border-gray-200 text-gray-500 flex-shrink-0">
                {getFileIcon(file_name)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{file_name}</p>
                <p className="text-xs text-gray-500">{getFileTypeLabel(file_name)}</p>
              </div>
            </div>

            {file?.summary && <div className="mt-3 text-xs text-gray-600">{file.summary}</div>}
          </div>
        )}

        {activeTab === "code" && file?.code && (
          <div role="tabpanel" id="tabpanel-code" aria-label="Code content">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-medium text-gray-700">Python Code</h4>
              <CopyButton text={file.code} variant="light" size="sm" />
            </div>
            <div className="h-full overflow-hidden rounded-sm">
              {!isLoading && (
                <Highlight
                  theme={themes.dracula}
                  code={file.code}
                  language="python"
                  prism={PrismInstance as any}
                >
                  {({ style, tokens, getLineProps, getTokenProps, className }) => (
                    <pre
                      className={`not-prose h-full p-4 overflow-x-auto text-xs font-mono leading-relaxed ${
                        className || ""
                      }`}
                      style={style}
                    >
                      {tokens.map((line, i) => (
                        <div
                          {...getLineProps({ line, key: i })}
                          key={`line-${i}`}
                          style={{ minHeight: "1.5em" }}
                        >
                          {line.map((token, key) => (
                            <span {...getTokenProps({ token, key })} key={`token-${key}`} />
                          ))}
                        </div>
                      ))}
                    </pre>
                  )}
                </Highlight>
              )}
              {isLoading && (
                <div className="h-full p-4 bg-gray-800 text-gray-300 text-sm font-mono leading-relaxed">
                  Loading syntax highlighting...
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "lineage" && file?.artifact_id && (
          <div role="tabpanel" id="tabpanel-lineage" aria-label="Lineage content">
            <LineageTab artifactId={file.artifact_id} />
          </div>
        )}
      </div>

      {/* Debug Section */}
      {debugMode && (
        <div className="text-gray-800">
          <DetailsSection
            content={
              <div className="space-y-3 pt-2 pb-4 px-4 rounded-lg border border-gray-200">
                <DetailField label="File Data" content={file} />
              </div>
            }
            defaultExpanded={false}
          />
        </div>
      )}
    </div>
  );
}
